<template>
  <div class="min-h-screen bg-gray-50 flex flex-col items-center py-8">
    <!-- Add Magazine Button -->
    <div class="w-full max-w-4xl mb-6">
      <button
        @click="openModal('add')"
        class="py-2.5 px-6 bg-purple-600 hover:bg-purple-700 text-white font-semibold rounded-lg shadow-md transition"
      >
        📘 Add Magazine
      </button>
    </div>

    <!-- Modal -->
    <div
      v-if="showModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="closeModal"
    >
      <div class="bg-white shadow-lg rounded-2xl w-full max-w-lg p-8">
        <h2 class="text-2xl font-semibold text-center mb-6">
          {{ modalMode === 'add' ? '📘 Upload Magazine' : '✏️ Update Magazine' }}
        </h2>

        <form
          @submit.prevent="modalMode === 'add' ? uploadMagazine() : updateMagazine()"
          class="space-y-5"
        >
          <!-- Title -->
          <input
            v-model="title"
            required
            placeholder="Magazine title"
            class="w-full px-4 py-2 border rounded-lg"
          />

          <!-- Description -->
          <textarea
            v-model="description"
            rows="3"
            placeholder="Short description"
            class="w-full px-4 py-2 border rounded-lg"
          ></textarea>

          <!-- PDF -->
          <input
            ref="pdfInput"
            type="file"
            accept="application/pdf"
            @change="onPDFChange"
            required
          />

          <!-- Image -->
          <input
            ref="imageInput"
            type="file"
            accept="image/*"
            @change="onImageChange"
          />

          <img
            v-if="imagePreview"
            :src="imagePreview"
            class="w-28 h-28 object-cover rounded mx-auto"
          />

          <!-- Buttons -->
          <div class="flex gap-4">
            <button
              type="submit"
              :disabled="loading"
              class="w-full bg-purple-600 text-white py-2 rounded"
            >
              {{ loading ? 'Saving...' : modalMode === 'add' ? 'Upload' : 'Update' }}
            </button>
            <button
              type="button"
              @click="closeModal"
              class="w-full bg-gray-300 py-2 rounded"
            >
              Cancel
            </button>
          </div>
        </form>

        <p v-if="success" class="text-green-600 mt-3 text-center">{{ success }}</p>
        <p v-if="error" class="text-red-600 mt-3 text-center">{{ error }}</p>
      </div>
    </div>

    <!-- Magazine Table -->
    <div class="bg-white shadow-lg rounded-2xl w-full max-w-4xl p-8">
      <h2 class="text-2xl font-semibold mb-6 text-center">📚 All Magazines</h2>

      <table v-if="magazines.length" class="w-full table-auto">
        <tr class="bg-gray-100">
          <th class="p-2">Title</th>
          <th class="p-2">Description</th>
          <th class="p-2">PDF</th>
          <th class="p-2">Actions</th>
        </tr>

        <tr v-for="mag in magazines" :key="mag.id" class="border-b">
          <td class="p-2">{{ mag.title }}</td>
          <td class="p-2">{{ mag.description }}</td>
          <td class="p-2">
            <a :href="mag.pdf_path" target="_blank" class="text-blue-600">
              View PDF
            </a>
          </td>
          <td class="p-2 flex gap-2">
            <button
              @click="openModal('update', mag)"
              class="bg-yellow-500 text-white px-3 py-1 rounded"
            >
              Edit
            </button>
            <button
              @click="confirmDelete(mag.id)"
              class="bg-red-500 text-white px-3 py-1 rounded"
            >
              Delete
            </button>
          </td>
        </tr>
      </table>

      <p v-else class="text-center text-gray-500">No magazines found.</p>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import Swal from 'sweetalert2';

export default {
  name: "UploadMagazine",
  data() {
    return {
      title: "",
      description: "",
      pdfFile: null,
      imageFile: null,
      imagePreview: null,
      magazines: [],
      loading: false,
      success: "",
      error: "",
      showModal: false,
      modalMode: "add",
      selectedMagazineId: null,
    };
  },
  methods: {
    onPDFChange(e) {
      this.pdfFile = e.target.files[0];
    },
    onImageChange(e) {
      this.imageFile = e.target.files[0];
      this.imagePreview = URL.createObjectURL(this.imageFile);
    },

    async uploadMagazine() {
      this.loading = true;
      const formData = new FormData();
      formData.append("title", this.title);
      formData.append("description", this.description);
      formData.append("pdfFile", this.pdfFile);
      if (this.imageFile) formData.append("imageFile", this.imageFile);

      try {
        await axios.post(
          `${process.env.VUE_APP_API_BASE_URL}/upload-magazine`,
          formData
        );
        this.success = "Magazine uploaded successfully";
        this.fetchMagazines();
        this.closeModal();
      } catch {
        this.error = "Upload failed";
      } finally {
        this.loading = false;
      }
    },

    async updateMagazine() {
      const formData = new FormData();
      formData.append("title", this.title);
      formData.append("description", this.description);
      if (this.pdfFile) formData.append("pdfFile", this.pdfFile);
      if (this.imageFile) formData.append("imageFile", this.imageFile);

      await axios.put(
        `${process.env.VUE_APP_API_BASE_URL}/magazines/${this.selectedMagazineId}`,
        formData
      );

      this.fetchMagazines();
      this.closeModal();
    },

    async fetchMagazines() {
      const res = await axios.get(
        `${process.env.VUE_APP_API_BASE_URL}/magazines`
      );
      this.magazines = res.data;
    },

    async deleteMagazine(id) {
      try {
        await axios.delete(
          `${process.env.VUE_APP_API_BASE_URL}/magazines/${id}`
        );
        this.fetchMagazines();
        Swal.fire({
          icon: 'success',
          title: 'Deleted!',
          text: 'Magazine deleted successfully.',
          confirmButtonColor: '#BF3131'
        });
      } catch (err) {
        console.error("Error deleting magazine:", err);
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: 'Failed to delete magazine.',
          confirmButtonColor: '#BF3131'
        });
      }
    },

    async confirmDelete(id) {
      const result = await Swal.fire({
        title: 'Delete Magazine?',
        text: "Are you sure you want to delete this magazine?",
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#d33',
        cancelButtonColor: '#3085d6',
        confirmButtonText: 'Yes, delete it!'
      });

      if (result.isConfirmed) {
        await this.deleteMagazine(id);
      }
    },

    openModal(mode, mag = null) {
      this.showModal = true;
      this.modalMode = mode;

      if (mode === "update") {
        this.selectedMagazineId = mag.id;
        this.title = mag.title;
        this.description = mag.description;
        this.imagePreview = mag.thumbnail_path;
      }
    },

    closeModal() {
      this.showModal = false;
      this.title = "";
      this.description = "";
      this.pdfFile = null;
      this.imageFile = null;
      this.imagePreview = null;
      this.selectedMagazineId = null;
    },
  },
  mounted() {
    this.fetchMagazines();
  },
};
</script>
